// Featured/browse card: 16:9 gradient thumbnail + meta strip, rounded glass, a
// motion hover-lift and azure hairline on hover. Thumbnails are seeded gradients
// (the backend stores no thumbnail), so each stream gets a stable, distinct hue.
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import type { StreamCardDTO } from "../api/types.js";
import { easeOut, riseItem } from "@/lib/motion";
import { Avatar } from "./ui/Avatar.js";
import { StatusBadge } from "./ui/LiveBadge.js";
import { ViewerCount } from "./ui/ViewerCount.js";
import { formatCount, gradientFor } from "./ui/format.js";

function scheduledLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function StreamCard({ card }: { card: StreamCardDTO }) {
  const to = `/watch/${card.slug ?? card.uuid}`;
  return (
    <motion.div
      variants={riseItem}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2, ease: easeOut }}
      className="group"
    >
      <Link
        to={to}
        className="block overflow-hidden rounded-2xl border border-white/[0.07] bg-card/60 outline-none backdrop-blur-md transition-colors duration-200 hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring [box-shadow:0_24px_60px_-32px_hsla(220,60%,2%,0.9)] group-hover:shadow-glow"
      >
        {/* 16:9 thumbnail (seeded gradient stand-in) */}
        <div className="relative aspect-video w-full overflow-hidden">
          <div
            className="absolute inset-0 transition-transform duration-300 ease-out group-hover:scale-[1.04]"
            style={{ backgroundImage: gradientFor(card.uuid + card.title) }}
          />
          <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,transparent,hsla(220,60%,2%,0.5))]" />

          <div className="absolute left-2.5 top-2.5">
            <StatusBadge status={card.status} />
          </div>

          <div className="absolute bottom-2.5 right-2.5">
            {card.status === "live" ? (
              <span className="rounded-full bg-black/45 px-2 py-0.5 backdrop-blur-sm">
                <ViewerCount count={card.viewerCount} />
              </span>
            ) : card.status === "scheduled" && card.scheduledStartAt ? (
              <span className="rounded-full bg-black/45 px-2 py-0.5 font-mono text-xs text-warn backdrop-blur-sm">
                {scheduledLabel(card.scheduledStartAt)}
              </span>
            ) : null}
          </div>

          {/* Hover play affordance */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-glow">
              <Play className="h-5 w-5 translate-x-0.5 fill-current" />
            </span>
          </div>
        </div>

        {/* Meta strip */}
        <div className="flex items-center gap-2.5 border-t border-white/[0.06] p-3">
          <Avatar src={card.author.avatar} name={card.author.name} size={34} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{card.title}</p>
            <p className="truncate text-xs text-muted-foreground">
              {card.author.name}
              {card.region ? (
                <span className="text-muted-foreground/60"> · {card.region}</span>
              ) : null}
            </p>
          </div>
          {card.status === "live" ? (
            <span className="ml-auto shrink-0 font-mono text-xs tabular-nums text-muted-foreground/70">
              {formatCount(card.viewerCount)}
            </span>
          ) : null}
        </div>
      </Link>
    </motion.div>
  );
}
